import {
  MouseEvent,
  MouseEventHandler,
  SyntheticEvent,
  useCallback,
} from 'react';
import {useDispatch} from 'react-redux';
import {t} from '../../../common/getTranslations';
import {requestContext} from '../../../common/requestContext';
import useTypedSelector from '../../../common/useTypedSelector';
import {ButtonV2Variant} from '../../../design-system/atoms/ButtonV2/ButtonV2.enum';
import {Text} from '../../../design-system/atoms/Text/Text';
import {Dialog} from '../../../design-system/organisms/Dialog';
import {PcRecentSearchesItem} from '../../../design-system/organisms/PcRecentSearchesItem/PcRecentSearchesItem';
import {PcSearchesToolbar} from '../../../design-system/organisms/PcSearchesToolbar/PcSearchesToolbar';
import {RecentSearches} from '../../../design-system/organisms/RecentSearches/RecentSearches';
import {RecentSearchesItemList} from '../../../design-system/organisms/RecentSearchesItemList/RecentSearchesItemList';
import {RecentSearchesToolbar} from '../../../design-system/organisms/RecentSearchesToolbar/RecentSearchesToolbar';
import {useDialog} from '../../../dialog/useDialog.hook';
import {LOG_SEARCH_CLICK} from '../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {addRecentKeyword} from '../../../recentSearchKeyword/recentKeywordList.storage';
import {useAutoSaveRecentKeyword} from '../../../recentSearchKeyword/useNotSaveRecentKeyword.hook';
import {useRecentSearchKeyword} from '../../../recentSearchKeyword/useRecentSearchKeyword.hook';
import {setOpenPcSearchModal, setOpenSearchDialog} from '../state/searchSlice';

export function RecentSearchesContainer() {
  const dispatch = useDispatch();
  const {autoSaveRecentKeyword, setAutoSaveRecentKeyword} =
    useAutoSaveRecentKeyword();
  const {recentKeywordList, removeAllRecentKeyword, removeRecentKeyword} =
    useRecentSearchKeyword(autoSaveRecentKeyword);

  const onClickRecentKeyword = useCallback((keyword: string) => {
    addRecentKeyword(keyword);
  }, []);
  const onClickRemoveRecentKeyword = useCallback(
    (keyword: string) => {
      removeRecentKeyword(keyword);
    },
    [removeRecentKeyword]
  );

  const isMobile = requestContext.requestInfo.display.isMobile;
  const {openSearchDialog} = useTypedSelector(state => state.search);

  const onClosePcSearchDialog = useCallback(
    (event?: MouseEvent) => {
      event?.stopPropagation();
      dispatch(setOpenSearchDialog(false));
    },
    [dispatch]
  );

  const onClickSaveOrNotRecentKeyword = useCallback(
    (event: SyntheticEvent) => {
      LOG_SEARCH_CLICK(
        '저장기능_클릭',
        event,
        () => {
          dispatch(setOpenSearchDialog(true));
        },
        {
          customPage: '검색_메인',
        }
      );
    },
    [dispatch]
  );

  const onClickConfirmNotSaveRecentKeyword = useCallback(() => {
    setAutoSaveRecentKeyword(!autoSaveRecentKeyword);
    dispatch(setOpenSearchDialog(false));
  }, [autoSaveRecentKeyword, dispatch, setAutoSaveRecentKeyword]);

  const onClickPcSearchClose = useCallback(() => {
    dispatch(setOpenPcSearchModal(false));
  }, [dispatch]);

  const {
    DialogWrapper: RemoveRecentKeywordDialogWrapper,
    showDialog: showRemoveAllRecentKeywordDialog,
    hideDialog: hideRemoveAllRecentKeywordDialog,
  } = useDialog({isMobile});

  const onClickRemoveAllRecentKeyword: MouseEventHandler = useCallback(
    e => {
      LOG_SEARCH_CLICK('전체삭제_클릭', e, showRemoveAllRecentKeywordDialog, {
        customPage: '검색_메인',
      });
    },
    [showRemoveAllRecentKeywordDialog]
  );

  const onClickCancelRemoveAllRecentKeyword = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      hideRemoveAllRecentKeywordDialog();
    },
    [hideRemoveAllRecentKeywordDialog]
  );

  const onClickConfirmRemoveAllRecentKeyword = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      removeAllRecentKeyword();
      hideRemoveAllRecentKeywordDialog();
    },
    [hideRemoveAllRecentKeywordDialog, removeAllRecentKeyword]
  );

  return (
    <>
      {isMobile && (
        <RecentSearches
          className="pt-24pxr"
          {...putTiaraObjectProps({
            click: {layer1: '최근검색어'},
          })}
        >
          <RecentSearchesToolbar
            className="px-15pxr"
            autoSaveRecentKeyword={autoSaveRecentKeyword}
            onClickRemoveAllRecentKeyword={onClickRemoveAllRecentKeyword}
            onClickSaveOrNotRecentKeyword={onClickSaveOrNotRecentKeyword}
            shouldShowRemoveAllRecentKeyword={
              autoSaveRecentKeyword && recentKeywordList.length > 0
            }
          />
          <RecentSearchesItemList
            className="mt-14pxr mb-24pxr"
            itemList={recentKeywordList}
            autoSaveRecentKeyword={autoSaveRecentKeyword}
            onClickKeyword={onClickRecentKeyword}
            onClickRemoveButton={onClickRemoveRecentKeyword}
          />
        </RecentSearches>
      )}
      {!isMobile && (
        <div
          className="flex flex-1 flex-col"
          {...putTiaraObjectProps({
            click: {
              layer1: '최근검색어',
            },
          })}
        >
          {!autoSaveRecentKeyword ||
          (autoSaveRecentKeyword && !recentKeywordList.length) ? (
            <div className="flex flex-1 flex-col justify-center">
              <Text className="font-small1 pb-12pxr text-center text-theme-solid-60">
                {!autoSaveRecentKeyword && t('search_recent_keyword_off_save')}
                {autoSaveRecentKeyword &&
                  !recentKeywordList.length &&
                  t('search_recent_keyword_empty')}
              </Text>
            </div>
          ) : null}
          {autoSaveRecentKeyword && !!recentKeywordList.length && (
            <div className="my-8pxr mx-11pxr flex flex-1 flex-col">
              {recentKeywordList.map(({keyword, createdAt}, index) => (
                <PcRecentSearchesItem
                  key={keyword}
                  index={index}
                  keyword={keyword}
                  createdAt={createdAt}
                  onClickKeyword={keyword => onClickRecentKeyword(keyword)}
                  onClickRemoveButton={keyword => removeRecentKeyword(keyword)}
                  searchUrl={`/search/result?keyword=${encodeURIComponent(
                    keyword
                  )}`}
                />
              ))}
            </div>
          )}
          <PcSearchesToolbar
            autoSaveRecentKeyword={autoSaveRecentKeyword}
            shouldShowRemoveAllRecentKeyword={
              autoSaveRecentKeyword && recentKeywordList.length > 0
            }
            onClickSaveOrNotRecentKeyword={onClickSaveOrNotRecentKeyword}
            onClickRemoveAllRecentKeyword={onClickRemoveAllRecentKeyword}
            onClickClose={onClickPcSearchClose}
          />
        </div>
      )}
      <Dialog
        isOpen={openSearchDialog}
        onClose={onClosePcSearchDialog}
        isMobile={isMobile}
      >
        <Dialog.Dimmed />
        <Dialog.Title>
          {autoSaveRecentKeyword
            ? t('search_before_off_save_popup')
            : t('search_before_on_save_popup')}
        </Dialog.Title>
        <Dialog.List>
          <Dialog.Label>
            {autoSaveRecentKeyword
              ? t('search_before_off_save_detail_popup')
              : t('search_before_on_save_detail_popup')}
          </Dialog.Label>
        </Dialog.List>
        <Dialog.BottomCombination>
          <Dialog.Button onClick={onClosePcSearchDialog}>
            {t('common_cancel_button')}
          </Dialog.Button>
          <Dialog.Button
            onClick={onClickConfirmNotSaveRecentKeyword}
            variant={ButtonV2Variant.Primary}
          >
            {t('common_confirm_button')}
          </Dialog.Button>
        </Dialog.BottomCombination>
      </Dialog>

      <RemoveRecentKeywordDialogWrapper>
        <Dialog.Dimmed />
        <Dialog.Title>{t('search_recent_confirm_delete_all')}</Dialog.Title>
        <Dialog.BottomCombination>
          <Dialog.Button onClick={onClickCancelRemoveAllRecentKeyword}>
            {t('common_cancel_button')}
          </Dialog.Button>
          <Dialog.Button
            onClick={onClickConfirmRemoveAllRecentKeyword}
            variant={ButtonV2Variant.Primary}
          >
            {t('common_confirm_button')}
          </Dialog.Button>
        </Dialog.BottomCombination>
      </RemoveRecentKeywordDialogWrapper>
    </>
  );
}
